import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import type { LegalAdviceFormSevenAData } from '../../../types/LegalAdvices/LegalAdviceFormSevenA.type';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { fetchLegalAdviceById, saveLegalAdviceDraft, submitLegalAdviceForm } from '../../../services/LegalAdviceFormSevenAService';
// import { FormDatePicker } from '../../../components/Form/FormDatePicker';

const INITIAL_LEGAL_ADVICE_FORM_DATA: LegalAdviceFormSevenAData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  office: '',
    registerNo: '',
    adviceCaseNo: '',
    originalCourtAndCaseNo: '',
    prosecutedLawSection: '',
    chargedLawSection: '',
    appealCourtAndCaseNo: '',
    acquittalDate: '',
    regionStateCourtAndCaseNo: '',
    dismissalOrderDate: '',
    submitterName: '',
    applicationLetterDate: '',
    judgmentCopyApplyDate: '',
    judgmentCopyReceiveDate: '',
    reviewSubmitDeadline: '',
    reviewSubmitDate: '',
    respondentDetails: '',
    caseSummary: '',
    prosecutionWitnessStatements: '',
    defenseWitnessStatements: '',
    courtWitnessStatements: '',
    advocateOpinionProsecution: '',
    advocateOpinionDefense: '',
    exhibitsReview: '',
    courtJudgmentReviewOriginal: '',
    courtJudgmentReviewAppeal: '',
    courtJudgmentReviewHighCourt: '',
    appealFitnessOpinion: '',

    handlingSignName: '',
    handlingSubmitDate: '',
    handlingSignatureImage: '',

    regionSignName: '',
    regionReceiveDate: '',
    regionSubmitDate: '',
    regionSignatureImage: '',
    centralOfficerOpinion: '',
    centralOfficerSignName: '',
    centralOfficerReceiveDate: '',
    centralOfficerSubmitDate: '',
    centralOfficerSignatureImage: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormSevenAData>;
}

export function LegalFormSevenA({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormSevenAData>({
    ...INITIAL_LEGAL_ADVICE_FORM_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
   const [activeSigningRole, setActiveSigningRole] = useState<'handling' | 'central' | 'region' | null>(null);
  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceById(id)
      .then((data: LegalAdviceFormSevenAData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.centralOfficerSignatureImage?.trim() && 
    (formData.centralOfficerSignName?.trim() && formData.regionSignatureImage?.trim() && formData.regionSignName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormSeven'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-seven');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleClearDate = (name: keyof LegalAdviceFormSevenAData) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({ ...prev, [name]: '' }));
  // };

  const handleOpenSignatureModel = (role: 'handling' | 'region' | 'central') => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };

  // const handleClearSignature = (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   if (!isEditable) return;
  //   setFormData((prev) => ({ ...prev, signatureImage: '' }));
  // };

  const handleClearHandlingSignature = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, handlingSignatureImage: '' }));
    };

  const handleClearRegionSignature = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, regionSignatureImage: '' }));
    };
  
    const handleClearCentralOfficerSignature = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, centralOfficerSignatureImage: '' }));
    };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };

  const handleSaveSignature = () => {
    if (sigCanvasRef.current) {
      // Replace getTrimmedCanvas() with getCanvas()
      const canvas = sigCanvasRef.current.getCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      
      if (activeSigningRole === 'central') {
        setFormData((prev) => ({ ...prev, centralOfficerSignatureImage: dataUrl }));
      } else if (activeSigningRole === 'region')   {
        setFormData((prev) => ({ ...prev, regionSignatureImage: dataUrl }));
      }else{
        setFormData((prev) => ({ ...prev, handlingSignatureImage: dataUrl }));
      }
      
      setIsSigning(false);
      setActiveSigningRole(null);
    }
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormSevenAData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.handlingSubmitDate?.trim() ||
      !formData.centralOfficerSubmitDate?.trim() ||
      !formData.regionSubmitDate?.trim()
    ) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    const nameOfSigner = (formData.signerName || formData.signName || '').trim();
    if (!isFormSigned) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormSevenAData = {
        ...formData,
        id: targetId || '',
        // signerName: nameOfSigner,
        // signName: nameOfSigner,
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.handlingSubmitDate?.trim() ||
      !formData.centralOfficerSubmitDate?.trim() ||
      !formData.regionSubmitDate?.trim()
    ) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
      const levelsToSubmit =
        targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

      // Validate that at least one checkbox level is selected
      if (!levelsToSubmit || levelsToSubmit.length === 0) {
        alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
        return;
      }


    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      // const nameOfSigner = (formData.signerName || formData.signName || '').trim();
      // const hasSigned = Boolean(formData.signatureImage?.trim() && nameOfSigner);
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';

      if(!isFormSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }

      const payload: LegalAdviceFormSevenAData = {
        ...formData,
        id: targetId,
        // signerName: nameOfSigner,
        // signName: nameOfSigner,
        status: 'SUBMITTED',
        submitDate: formData.centralOfficerSubmitDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      //navigate('/legal-form-seven');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleClearDate = (name: keyof LegalAdviceFormSevenAData) => {
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, [name]: '' }));
    };

  const renderDatePicker = (
      name: Extract<keyof LegalAdviceFormSevenAData, string>
    ) => (
      <FormDatePicker
        name={name}
        value={(formData?.[name] as string) || ''}
        isEditable={isEditable}
        onChange={handleChange}
        onClear={handleClearDate}
      />
    );

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
    const status = formData.status || 'DRAFT';

    // Extract and format Geo Level text
    const rawGeo = formData.targetGeoLevels || formData.geoLevel;
    const formatGeo = (geo: any) => {
      if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
      if (Array.isArray(geo)) {
        return geo
          .map((g) => GEO_LEVEL_MAP[g] || g)
          .filter(Boolean)
          .join(' | ');
      }
      return '';
    };
    const geoText = formatGeo(rawGeo);

    const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
    <PageLayoutWrapper
      handleBackNavigation={handleBackNavigation}
      renderStageProgression={renderStageProgression}
      isEditable={isEditable}
      isPreview={isPreview}
      readOnlyMessage={
        formData.status === 'APPROVED'
          ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
          : formData.status === 'SUBMITTED'
            ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : undefined
      }
      setIsPreview={setIsPreview}
    >
      <div className="w-full max-w-[850px] bg-white shadow-lg border border-gray-300 p-12 text-black space-y-16 text-xs md:text-sm leading-relaxed">
        
        {/* ==================== စာမျက်နှာ ၁ ==================== */}
        <div>
          <div className="text-right text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၇-က)</div>
          
          <div className="text-center space-y-2 mt-2">
            <h1 className="text-base font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
            <h2 className="text-sm font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
            <div className="flex justify-center items-center gap-2">
              <DatalistInput
                name="office"
                value={formData.office}
                onChange={(e) => setFormData({ ...formData, office: e.target.value })}
                placeholder=""
                options={OFFICE_OPTIONS}
              />
              <span className="font-bold">ရုံး</span>
            </div>
            <h3 className="text-sm font-bold pt-4 max-w-2xl mx-auto leading-normal">
              အပြီးအပြတ်လွတ်သည့်အမိန့်အား အယူခံမှုကို ပလပ်သည့် အမိန့်အပေါ် အယူခံမှု တင်သွင်းရန် သင့် မသင့် စိစစ်တင်ပြခြင်း
            </h3>
          </div>

          {/* အမှတ်စဉ် ၁ မှ ၁၆ */}
          <div className="mt-8 space-y-4">
            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁။</span>
              <span className="flex-1">ဥပဒေရုံး၏ အယူခံမှုတင်သွင်းရန် သင့်မသင့် စိစစ်ခြင်း မှတ်ပုံတင်အမှတ်စဉ်</span>
              <input type="text" name="registerNo" value={formData.registerNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၂။</span>
              <span className="flex-1">အမှုလိုက်စာတွဲအမှတ်</span>
              <input type="text" name="adviceCaseNo" value={formData.adviceCaseNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၃။</span>
              <span className="flex-1">မူလတရားရုံးအမည်နှင့် အမှုအမှတ်</span>
              <input type="text" name="originalCourtAndCaseNo" value={formData.originalCourtAndCaseNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၄။</span>
              <span className="flex-1">တရားစွဲဆိုထားသည့်ဥပဒေ၊ ပုဒ်မ</span>
              <input type="text" name="prosecutedLawSection" value={formData.prosecutedLawSection} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၅။</span>
              <span className="flex-1">စွဲချက်တင်သည့် ဥပဒေ၊ ပုဒ်မ</span>
              <input type="text" name="chargedLawSection" value={formData.chargedLawSection} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၆။</span>
              <span className="flex-1">အယူခံ၊ ပြင်ဆင်တရားရုံးအမည်နှင့် အမှုအမှတ်</span>
              <input type="text" name="appealCourtAndCaseNo" value={formData.appealCourtAndCaseNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၇။</span>
              <span className="flex-1">အပြီးအပြတ်လွတ်သည့် အမိန့်ချမှတ်သည့်ရက်စွဲ</span>
              <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('acquittalDate')}</div>
              {/* <input type="text" name="acquittalDate" value={formData.acquittalDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၈။</span>
              <span className="flex-1">တိုင်းဒေသကြီး / ပြည်နယ်တရားလွှတ်တော်အမည်နှင့် အမှုအမှတ်</span>
              <input type="text" name="regionStateCourtAndCaseNo" value={formData.regionStateCourtAndCaseNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၉။</span>
              <span className="flex-1">အပြီးအပြတ်လွတ်သည့်အမိန့်အား အယူခံမှုကို ပလပ်သည့် အမိန့်ချမှတ်သည့် ရက်စွဲ</span>
              <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('dismissalOrderDate')}</div>
              {/* <input type="text" name="dismissalOrderDate" value={formData.dismissalOrderDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁၀။</span>
              <span className="flex-1">အယူခံမှုတင်သွင်းရန် တင်ပြလျှောက်ထားသူ</span>
              <input type="text" name="submitterName" value={formData.submitterName} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁၁။</span>
              <span className="flex-1">အယူခံမှုတင်သွင်းရန်လျှောက်လွှာ / အကြောင်းကြားစာ ရရှိသည့်ရက်စွဲ (ရှိလျှင်)</span>
                <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('applicationLetterDate')}</div>
              {/* <input type="text" name="applicationLetterDate" value={formData.applicationLetterDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁၂။</span>
              <span className="flex-1">စီရင်ချက်မိတ္တူလျှောက်ထားသည့်ရက်စွဲ</span>
                <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('judgmentCopyApplyDate')}</div>
              {/* <input type="text" name="judgmentCopyApplyDate" value={formData.judgmentCopyApplyDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁၃။</span>
              <span className="flex-1">စီရင်ချက်မိတ္တူရရှိသည့်ရက်စွဲ</span>
                <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('judgmentCopyReceiveDate')}</div>
              {/* <input type="text" name="judgmentCopyReceiveDate" value={formData.judgmentCopyReceiveDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁၄။</span>
              <span className="flex-1">အယူခံမှုတင်သွင်းရန် သင့် မသင့် စိစစ်ချက်တင်ပြရမည့်ရက်စွဲ</span>
                <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('reviewSubmitDeadline')}</div>
              {/* <input type="text" name="reviewSubmitDeadline" value={formData.reviewSubmitDeadline} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁၅။</span>
              <span className="flex-1">အယူခံမှုတင်သွင်းရန် သင့် မသင့် စိစစ်ချက်တင်ပြသည့်ရက်စွဲ</span>
                <div className="w-1/3 flex-grow px-2 text-center">{renderDatePicker('reviewSubmitDate')}</div>
              <input type="text" name="reviewSubmitDate" value={formData.reviewSubmitDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex flex-col space-y-1">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၁၆။</span>
                <span className="flex-1">အယူခံမှုတင်သွင်းရန် လျှောက်ထားခံရသူ (များ)၏ အမည်၊ အဘအမည်၊ အကျဥ်းသားနံပါတ်၊အကျဥ်းထောင်အမည်နှင့်နောက်ဆုံး နေရပ်လိပ်စာအပြည့်အစုံ -</span>
              </div>
              <textarea
                name="respondentDetails"
                value={formData.respondentDetails}
                onChange={handleChange}
                rows={4}
                className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1"
                placeholder=""
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-300" />

        {/* ==================== စာမျက်နှာ ၂ ==================== */}
        <div className="space-y-6">
          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၁၇။</span>အမှုဖြစ်စဉ်အကျဉ်း</label>
            <textarea name="caseSummary" value={formData.caseSummary} onChange={handleChange} rows={5} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၁၈။</span>တရားလိုဘက်မှ သက်သေများ၏ ထွက်ဆိုချက်များ</label>
            <p className="text-gray-500 text-xs pb-1">တရားလိုနှင့် တရားလိုပြသက်သေတစ်ဦးစီ၏ အရေးပါသောထွက်ဆိုချက် (ပြန်လှန်စစ်ဆေးရာတွင် ပျက်ပြယ်သွားခြင်း ရှိ မရှိ ဖော်ပြရန်)</p>
            <textarea name="prosecutionWitnessStatements" value={formData.prosecutionWitnessStatements} onChange={handleChange} rows={5} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၁၉။</span>တရားခံဘက်မှ သက်သေများ၏ ထွက်ဆိုချက်များ</label>
            <p className="text-gray-500 text-xs pb-1">တရားခံနှင့် တရားခံပြသက်သေတစ်ဦးစီ၏ အရေးပါသောထွက်ဆိုချက် အကျဉ်းနှင့် (တရားခံတင်ပြသည့် သက်သေခံ အထောက်အထားများ (ရှိလျှင်) ဖော်ပြရန်)</p>
            <textarea name="defenseWitnessStatements" value={formData.defenseWitnessStatements} onChange={handleChange} rows={5} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၂၀။</span>ရုံးခေါ်သက်သေ (ရှိလျှင်) ၏ ထွက်ဆိုချက်များ</label>
            <textarea name="courtWitnessStatements" value={formData.courtWitnessStatements} onChange={handleChange} rows={3} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="space-y-3">
            <label className="font-bold"><span className="mr-4">၂၁။</span>အမှုလိုက်ဥပဒေအရာရှိ၏ သုံးသပ်ချက်</label>
            <div className="pl-6 space-y-2">
              <div className="flex flex-col space-y-1">
                <span className="font-medium">(က) တရားလိုဘက်မှ တင်ပြချက်များအပေါ် သုံးသပ်ချက်</span>
                <textarea name="advocateOpinionProsecution" value={formData.advocateOpinionProsecution} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="font-medium">(ခ) တရားခံဘက်မှ တင်ပြချက်များအပေါ် သုံးသပ်ချက်</span>
                <textarea name="advocateOpinionDefense" value={formData.advocateOpinionDefense} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="font-medium">(ဂ) သက်သေခံပစ္စည်းနှင့် သက်သေခံစာရွက်စာတမ်းများအပေါ် သုံးသပ်ချက်</span>
                <textarea name="exhibitsReview" value={formData.exhibitsReview} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>

              <div className="space-y-3">
                <label className="font-bold"><span className="mr-4">(ဃ)</span>တရားရုံး၏ စီရင်ချက်နှင့် စပ်လျဉ်း၍ သုံးသပ်ချက်</label>
                <div className="pl-6 space-y-3">
                  <div className="flex flex-col space-y-1">
                    <span className="font-medium">(၁) မူလတရားရုံး စီရင်ချက်နှင့်စပ်လျဉ်း၍ သုံးသပ်ချက်</span>
                    <textarea name="courtJudgmentReviewOriginal" value={formData.courtJudgmentReviewOriginal} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
                  </div>
                  <div className="flex flex-col space-y-1">
                    <span className="font-medium">(၂) အယူခံ၊ ပြင်ဆင်တရားရုံး စီရင်ချက်နှင့်စပ်လျဉ်း၍ သုံးသပ်ချက်</span>
                    <textarea name="courtJudgmentReviewAppeal" value={formData.courtJudgmentReviewAppeal} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
                  </div>
                  <div className="flex flex-col space-y-1">
                    <span className="font-medium">(၃) တိုင်းဒေသကြီး သို့မဟုတ် ပြည်နယ်တရားလွှတ်တော် စီရင်ချက်နှင့်စပ်လျဉ်း၍ သုံးသပ်ချက်</span>
                    <textarea name="courtJudgmentReviewHighCourt" value={formData.courtJudgmentReviewHighCourt} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-1">
                <label className="font-bold"><span className="mr-4">(င)</span>အယူခံမှုတင်သွင်းရန် သင့်မသင့် သုံးသပ်တင်ပြချက်</label>
                <textarea name="appealFitnessOpinion" value={formData.appealFitnessOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>

            </div>
          </div>
        </div>

        {/* ==================== စာမျက်နှာ ၃ ==================== */}
        <div className="space-y-6">
          <div className="flex flex-col items-end pt-4">
            <div className="w-72 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModel('handling')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.handlingSignatureImage ? (
                    <img src={formData.handlingSignatureImage} alt="Region Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.handlingSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearHandlingSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="handlingSignName" value={formData.handlingSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">အမှုလိုက်ဥပဒေအရာရှိ</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('handlingSubmitDate')}</div>
              </div>
            </div>
          </div>

        </div>
        
        {/* ==================== စာမျက်နှာ ၄ ==================== */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၂၂။</span>အယူခံမှုတင်သွင်းရန် သင့် မသင့်နှင့် စပ်လျဉ်း၍ နှစ်ဖက် သက်သေခံချက် (များ)၊ တရားရုံး (များ)၏ စီရင်ချက် (များ)အပေါ် သုံးသပ်တင်ပြချက်</label>
            <textarea name="centralOfficerOpinion" value={formData.centralOfficerOpinion} onChange={handleChange} rows={5} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          {/* ဗဟိုအဆင့် တာဝန်ခံဥပဒေအရာရှိ လက်မှတ် */}
          <div className="flex flex-col items-end pt-4">
            <div className="w-72 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModel('central')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.centralOfficerSignatureImage ? (
                    <img src={formData.centralOfficerSignatureImage} alt="Central Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.centralOfficerSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearCentralOfficerSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="centralOfficerSignName" value={formData.centralOfficerSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">တာဝန်ခံဥပဒေအရာရှိ</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">လက်ခံရရှိသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('centralOfficerReceiveDate')}</div>
                {/* <input type="text" name="centralOfficerReceiveDate" value={formData.centralOfficerReceiveDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('centralOfficerSubmitDate')}</div>
                {/* <input type="text" name="centralOfficerSubmitDate" value={formData.centralOfficerSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>
        </div>

        {/* ==================== စာမျက်နှာ ၁၃၅ ==================== */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၂၃။</span>အယူခံမှုတင်သွင်းရန် သင့် မသင့်နှင့် စပ်လျဉ်း၍ သုံးသပ်တင်ပြချက်</label>
            <textarea name="centralOfficerOpinion" value={formData.centralOfficerOpinion} onChange={handleChange} rows={5} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          {/* တိုင်းဒေသကြီး/ပြည်နယ် ဥပဒေချုပ် လက်မှတ် */}
          <div className="flex flex-col items-end pt-4">
            <div className="w-72 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModel('region')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.regionSignatureImage ? (
                    <img src={formData.regionSignatureImage} alt="Region Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.regionSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearRegionSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="regionSignName" value={formData.regionSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">တိုင်းဒေသကြီး / ပြည်နယ်ဥပဒေချုပ်</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">လက်ခံရရှိသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('regionReceiveDate')}</div>
                {/* <input type="text" name="regionReceiveDate" value={formData.regionReceiveDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('regionSubmitDate')}</div>
                {/* <input type="text" name="regionSubmitDate" value={formData.regionSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-500 italic mt-4 pt-2">
            မှတ်ချက်။ ။ ဤပုံစံပါအမှတ်စဉ် ၁၆ မှ ၂၃ ထိ အချက်အလက်များကို စာရွက်လွတ်ဖြင့် ဖြည့်သွင်းရေးသားပြီး အစဉ်အတိုင်း ပူးတွဲတင်ပြရန်ဖြစ်ပါသည်။
          </div>
        </div>

        <FormActionButtons
            isPreview={isPreview}
            isEditable={isEditable}
            isSubmitting={isSubmitting}
            isFormSigned={isFormSigned}
            isApproved={isApproved}
            targetGeoLevels={formData.targetGeoLevels}
            currentGeoLevel={currentUser?.scopeLevel}
            selectedGeoLevels={selectedGeoLevels}
            onGeoLevelChange={setSelectedGeoLevels}
            onCancel={handleBackNavigation}
            onSaveDraft={handleSaveDraft}
            onApproveAndSave={handleApproveAndSave}
            onSubmitToSuperior={handleSubmitToSuperior}
          />

        {/* Attachment Section */}
        {/* <AttachmentSection
          attachments={formData.attachments}
          isEditable={isEditable}
          isPreview={isPreview}
          onFileChange={handleFileChange}
          onRemoveFile={handleRemoveFile}
        /> */}

        {/* Signature Modal */}
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => setIsSigning(false)}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleSaveSignature}
        />
{/* 
        <div className="absolute bottom-4 left-4 text-xs text-gray-400 print:bottom-0 print:left-0">
          လျှို့ဝှက်
        </div> */}

      </div>

    </PageLayoutWrapper>
  );
}